import type { Registry as Services } from '@ember/service';
import { tKey } from 'ember-intl';
import {
  MIN_LOS_RESTRICTION_TYPE,
  type MinLosRestrictionType,
} from 'lumen/utils/constants/pricing-assistant-constants';

interface FormatMinLosRecommendationOptions {
  displayedValue: number | null | undefined;
  intl: Services['intl'];
  recommendedValue: number | null | undefined;
}

interface FormatRestrictionValueOptions {
  intl: Services['intl'];
  value: number | null | undefined;
}

interface MinLosErrorMessageOptions {
  intl: Services['intl'];
  restrictionType: MinLosRestrictionType;
  value: number | null;
}

const CURRENT_IS_OPTIMAL_KEY = tKey(
  'pricing_manager.restrictions.current_is_optimal',
);
const NO_RECOMMENDATION_KEY = tKey('pricing_manager.no_recommendation');
const MIN_LOS_KEY = tKey('pricing_assistant.stay_restrictions.min_los');
const MIN_LOS_MIN_ERROR_KEY = tKey(
  'pricing_manager.restrictions.min_los_min_error',
);
const NO_MIN_LOS_KEY = tKey('pricing_manager.restrictions.no_min_los');
const REQUIRED_KEY = tKey('general.required');

/**
 * Formats the recommendation for a MinLOS restriction
 * - no recommendation -> "No recommendation"
 * - a recommendation matching the value already displayed -> "Current is optimal"
 * - else -> the formatted recommendation.
 */
export function formatMinLosRecommendation({
  displayedValue,
  intl,
  recommendedValue,
}: FormatMinLosRecommendationOptions): string {
  if (recommendedValue == null) {
    return intl.t(NO_RECOMMENDATION_KEY);
  }

  if (recommendedValue === displayedValue) {
    return intl.t(CURRENT_IS_OPTIMAL_KEY);
  }

  return formatMinLosValue({ intl, value: recommendedValue });
}

/**
 * Formats a MinLOS value for display: `null`/`undefined` -> "--", `0` -> "No MinLOS restriction",
 * else -> "MinLOS {count}".
 */
export function formatMinLosValue({
  intl,
  value,
}: FormatRestrictionValueOptions): string {
  if (value == null) {
    return intl.t('general.missing');
  }

  if (value === 0) {
    return intl.t(NO_MIN_LOS_KEY);
  }

  return intl.t(MIN_LOS_KEY, {
    count: value,
  });
}

/**
 * Formats an overbooking value for display: `null`/`undefined` -> "--", else -> the raw number.
 */
export function formatOverbookingValue({
  intl,
  value,
}: FormatRestrictionValueOptions): number | string {
  if (value == null) {
    return intl.t('general.missing');
  }

  return value;
}

/**
 * Validates a MinLOS input value: `No restriction` has no value to validate, a missing value is
 * required and a MinLOS below 2 is out of range. Returns `null` when the value is valid.
 */
export function minLosErrorMessage({
  intl,
  restrictionType,
  value,
}: MinLosErrorMessageOptions): string | null {
  if (restrictionType !== MIN_LOS_RESTRICTION_TYPE.MIN_LOS) {
    return null;
  }

  if (value === null) {
    return intl.t(REQUIRED_KEY);
  }

  if (value <= 1) {
    return intl.t(MIN_LOS_MIN_ERROR_KEY);
  }

  return null;
}
